import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  paymentFetchAPI,
  paymentCreateAPI,
  paymentUpdateAPI,
  paymentDeleteAPI,
} from "../../../api/payment/paymentApiHelper"
import Notify from "components/Common/notify"

// Robustly locate the list array + totalPages regardless of how the API nests
// them (handles shapes like response.data, response.data.items,
// response.data.items.data.items, etc.).
const extractList = root => {
  const visit = node => {
    if (!node || typeof node !== "object") return null
    if (Array.isArray(node)) return { items: node, totalPages: 1 }
    if (Array.isArray(node.items)) {
      return { items: node.items, totalPages: node.totalPages || 1 }
    }
    for (const key of ["data", "items", "result", "payload", "records"]) {
      if (node[key]) {
        const found = visit(node[key])
        if (found) return found
      }
    }
    return null
  }
  return visit(root) || { items: [], totalPages: 1 }
}

export const PaymentFetch = createAsyncThunk(
  "Payment/PaymentFetch",
  async data => {
    try {
      const response = await paymentFetchAPI(data)
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      Notify("error", "Failed to fetch Payment Data")
      throw Error("Failed to fetch Payment Data")
    }
  },
)

export const PaymentCreate = createAsyncThunk(
  "Payment/PaymentCreate",
  async data => {
    try {
      const response = await paymentCreateAPI(data)
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to create payment"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const PaymentUpdate = createAsyncThunk(
  "Payment/PaymentUpdate",
  async ({ id: id, data: data }) => {
    try {
      const response = await paymentUpdateAPI({ id: id, data: data })
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to update payment"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const PaymentDelete = createAsyncThunk(
  "Payment/PaymentDelete",
  async id => {
    try {
      const response = await paymentDeleteAPI(id)
      if (response?.status === 200) {
        Notify("success", "Payment deleted.")
        return { data: id, status: 200 }
      }
      throw Error("Failed to delete payment")
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to delete payment"
      Notify("error", errorMsg)
      throw Error("Failed to delete payment")
    }
  },
)

const initialState = {
  paymentData: [],
  totalPages: 1,
  loading: false,
  error: null,
}

export const PaymentSlice = createSlice({
  name: "PaymentSlice",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(PaymentFetch.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(PaymentFetch.fulfilled, (state, action) => {
        state.loading = false
        const { items, totalPages } = extractList(action.payload?.data)
        state.paymentData = Array.isArray(items) ? items : []
        state.totalPages = totalPages || 1
      })
      .addCase(PaymentFetch.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(PaymentCreate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(PaymentCreate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.status === 201 || action.payload?.status === 200) {
          const created = action.payload?.data?.data || action.payload?.data
          if (created && typeof created === "object" && created.id) {
            state.paymentData = [...state.paymentData, created]
          }
        }
      })
      .addCase(PaymentCreate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(PaymentUpdate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(PaymentUpdate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload && action.payload?.status === 200) {
          const updated = action.payload?.data?.data || action.payload?.data
          if (updated && typeof updated === "object" && updated.id) {
            state.paymentData = state.paymentData.map(item =>
              item.id === updated.id ? updated : item,
            )
          }
        }
      })
      .addCase(PaymentUpdate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(PaymentDelete.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(PaymentDelete.fulfilled, (state, action) => {
        state.loading = false
        state.paymentData = state.paymentData.filter(
          item => item?.id !== action.payload?.data,
        )
      })
      .addCase(PaymentDelete.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  },
})

export const {} = PaymentSlice.actions

export const allPayments = state => state.allPayments

export default PaymentSlice.reducer
